import React, { useState, useEffect, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import courseService from '../../../../services/CourseService'
import { CourseDeliveryDTO, CourseSetDTO } from '@beable/roster-backend-client-v1';
import CourseList from './CourseList';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';

const CourseBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

export interface CourseSetCourses {
  courseId: string;
  sort: number;
  grades?: string[];
}

interface CourseSetDialogProps {
  locationCode: string;
  courseSet?: CourseSetDTO;
  handleSelectionChange: ((courses: CourseSetCourses[]) => void);
}

export default function CourseSelection({ locationCode, courseSet, handleSelectionChange }: CourseSetDialogProps) {
  const [courses, setCourses] = useState<CourseDeliveryDTO[]>([]);
  const [availableCourses, setAvailableCourses] = useState<CourseDeliveryDTO[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<CourseDeliveryDTO[]>([]);

  useEffect(() => {
    const fetchCourses = async () => {
      setCourses([]);
      setAvailableCourses([]);

      const data = await courseService.getCoursesForLocation(locationCode);

      if (data) {
        if (isMounted) setCourses(data);

        if (courseSet && courseSet.courses) {
          const availableCourses: CourseDeliveryDTO[] = [];
          const selectedCourses: CourseDeliveryDTO[] = [];

          data.forEach(course => {
            const selected = courseSet.courses.find(c => c.courseId === course.courseId)
            if (selected) {
              selectedCourses[selected.sort - 1] = course;
            } else {
              availableCourses.push(course);
            }
          })

          if (isMounted) {
            setAvailableCourses(availableCourses);
            setSelectedCourses(selectedCourses);
          }

        } else {
          if (isMounted) setAvailableCourses(data);
        }
      }
    };

    let isMounted = true;
    fetchCourses();
    return () => { isMounted = false };
  }, [locationCode, courseSet]);

  const memoOnSelect = useCallback(handleSelectionChange, []);

  useEffect(() => {
      const selections = selectedCourses?.map((course, index) => {
        return { courseId: course.courseId, sort: index + 1, grades: course.grades };
      })

      if (selections) memoOnSelect(selections);
  }, [selectedCourses, memoOnSelect]);

  const saveList = (listId: string, list: CourseDeliveryDTO[]) => {
    if (listId === 'available-courses') setAvailableCourses(list);
    else setSelectedCourses(list);
  }

  const getList = (listId: string) => {
    if (listId === 'available-courses') return Array.from(availableCourses);
    else return Array.from(selectedCourses);
  }

  const onDragEnd = (result: DropResult) => {
    const { draggableId, source, destination } = result;
    const movedCourse = courses.find(course => course?.courseId === draggableId);

    // We don't have the course, don't have destination or item didn't move
    if (!destination || !movedCourse || (
        source.droppableId === destination.droppableId
        && source.index === destination.index
    )) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      const list = getList(source.droppableId);
      list.splice(source.index, 1);
      list.splice(destination.index, 0, movedCourse);

      saveList(source.droppableId ,list);
      return;
    }

    const sourceList = getList(source.droppableId);
    sourceList.splice(source.index, 1);
    saveList(source.droppableId, sourceList);

    const destinationList = getList(destination.droppableId);
    destinationList.splice(destination.index, 0, movedCourse);
    saveList(destination.droppableId, destinationList);
  }

  return (
    <CourseBox>
      <DragDropContext onDragEnd={onDragEnd}>
        <CourseList
          title='Available Courses'
          listId={'available-courses'}
          courses={availableCourses}
          showSort={false}
        />
        <CourseList
          title='Course Set'
          listId={'selected-courses'}
          courses={selectedCourses}
          showSort={true}
        />
      </DragDropContext>
    </CourseBox>
  )
}
